import React, { useCallback, useMemo, useState } from 'react';
import {
  computeSuspensionFrequencyHistograms,
  formatDuration,
  formatFrequencyRange,
} from '../utils/suspensionFrequencyAnalysis';
import './FrequencyHistogramPanel.css';

const BIN_COUNT_MIN = 8;
const BIN_COUNT_MAX = 48;
const BIN_COUNT_DEFAULT = 16;

function formatPct(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(1)}%`;
}

function FrequencyHistBlock({ title, histogram, binCount, selectedIndex, onSelectBar, accentClass }) {
  const bins = histogram?.bins;
  const maxPct = bins?.length ? Math.max(...bins.map((b) => b.percent), 0.01) : 1;

  if (!bins?.length) {
    return (
      <div className="suspension-freq-block">
        <div className="suspension-freq-title">{title}</div>
        <p className="suspension-freq-empty">Not enough data for a frequency histogram.</p>
      </div>
    );
  }

  return (
    <div className="suspension-freq-block">
      <div className="suspension-freq-title">{title}</div>
      <div className="suspension-freq-chart" role="list" aria-label={`${title} frequency histogram`}>
        {bins.map((bin, idx) => {
          const isSelected = selectedIndex === idx;
          const heightPct = Math.max(2, (bin.percent / maxPct) * 100);
          return (
            <button
              key={idx}
              type="button"
              role="listitem"
              className={`suspension-freq-bar-btn ${isSelected ? 'suspension-freq-bar-btn--selected' : ''}`}
              aria-pressed={isSelected}
              aria-label={`${formatFrequencyRange(bin.freqMin, bin.freqMax)}, ${formatPct(bin.percent)} of time`}
              onClick={() => onSelectBar(idx)}
            >
              <div
                className={`suspension-freq-bar ${accentClass}`}
                style={{ height: `${heightPct}%` }}
              />
              <span className="suspension-freq-bar-tick">
                {idx % Math.max(1, Math.ceil(binCount / 6)) === 0 || idx === bins.length - 1
                  ? formatFrequencyRange(bin.freqMin, bin.freqMax).replace(' Hz', '')
                  : ''}
              </span>
            </button>
          );
        })}
      </div>
      {selectedIndex != null && bins[selectedIndex] ? (
        <div className="suspension-freq-detail" role="status">
          <strong>{formatFrequencyRange(bins[selectedIndex].freqMin, bins[selectedIndex].freqMax)}</strong>
          <span>
            Duration: {formatDuration(bins[selectedIndex].durationSec)} ·{' '}
            {formatPct(bins[selectedIndex].percent)} of analyzed time
          </span>
        </div>
      ) : (
        <p className="suspension-freq-hint">Click a bar to see duration and percentage for that range.</p>
      )}
    </div>
  );
}

export default function FrequencyHistogramPanel({ series }) {
  const [binCount, setBinCount] = useState(BIN_COUNT_DEFAULT);
  const [selectedFront, setSelectedFront] = useState(null);
  const [selectedRear, setSelectedRear] = useState(null);

  const histograms = useMemo(
    () => (series ? computeSuspensionFrequencyHistograms(series, binCount) : null),
    [series, binCount],
  );

  const onBinCountChange = useCallback((e) => {
    setBinCount(Number(e.target.value));
    setSelectedFront(null);
    setSelectedRear(null);
  }, []);

  const meta =
    histograms?.front?.sampleRateHz != null
      ? `Sample rate ~${Math.round(histograms.front.sampleRateHz)} Hz · 0–${histograms.front.maxHz.toFixed(0)} Hz`
      : null;

  return (
    <div className="suspension-freq-panel">
      <div className="suspension-freq-controls">
        <label className="suspension-freq-slider-label" htmlFor="freq-bin-slider">
          Frequency ranges: <strong>{binCount}</strong> bars
        </label>
        <input
          id="freq-bin-slider"
          type="range"
          min={BIN_COUNT_MIN}
          max={BIN_COUNT_MAX}
          step={1}
          value={binCount}
          onChange={onBinCountChange}
          className="suspension-freq-slider"
        />
        <span className="suspension-freq-slider-hint">Fewer bars = wider ranges · more bars = finer detail</span>
      </div>
      {meta ? <p className="suspension-freq-meta">{meta}</p> : null}
      <p className="suspension-freq-desc">
        Distribution of suspension velocity energy across frequency bands (windowed FFT). Shows how much ride time
        falls in each Hz range.
      </p>
      <div className="suspension-freq-grid">
        <FrequencyHistBlock
          title="Front"
          histogram={histograms?.front}
          binCount={binCount}
          selectedIndex={selectedFront}
          onSelectBar={(idx) => setSelectedFront((prev) => (prev === idx ? null : idx))}
          accentClass="suspension-freq-bar--front"
        />
        <FrequencyHistBlock
          title="Rear"
          histogram={histograms?.rear}
          binCount={binCount}
          selectedIndex={selectedRear}
          onSelectBar={(idx) => setSelectedRear((prev) => (prev === idx ? null : idx))}
          accentClass="suspension-freq-bar--rear"
        />
      </div>
    </div>
  );
}
